import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { EmbedFrame } from "@/components/embed/EmbedFrame";
import { listAlgorithmUpdates } from "@/lib/backlink.functions";

const updatesQuery = queryOptions({
  queryKey: ["embed", "last-update"],
  queryFn: () => listAlgorithmUpdates(),
  staleTime: 5 * 60 * 1000,
});

export const Route = createFileRoute("/embed/last-update")({
  head: () => ({
    meta: [
      { title: "Last Local Algorithm Update — Map Observer embed" },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(updatesQuery),
  component: LastUpdateEmbed,
});

function daysAgo(iso: string) {
  return Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000));
}

function LastUpdateEmbed() {
  const { data } = useSuspenseQuery(updatesQuery);
  const confirmed = data.find((u) => u.status === "confirmed") ?? data[0];

  if (!confirmed) {
    return (
      <EmbedFrame name="last-update">
        <div style={{ padding: 16, fontSize: 13, color: "#3a342a" }}>
          No tracked updates yet.
        </div>
      </EmbedFrame>
    );
  }

  const d = daysAgo(confirmed.started_at);
  const sevColor = confirmed.severity >= 7 ? "#7c1d1d" : confirmed.severity >= 4 ? "#a04a1f" : "#2f6b3a";

  return (
    <EmbedFrame name="last-update">
      <div
        style={{
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          color: "#1a1612",
          border: "1px solid #d9cfbf",
          borderRadius: 6,
          background: "#fbf7ef",
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: sevColor,
            flexShrink: 0,
            boxShadow: `0 0 0 3px ${sevColor}22`,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: "#6b6357" }}>
            Last {confirmed.status} local update
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {confirmed.name}
          </div>
          <div style={{ fontSize: 11, color: "#6b6357", marginTop: 2 }}>
            {d === 0 ? "Today" : `${d} day${d === 1 ? "" : "s"} ago`} · severity {confirmed.severity}/10
          </div>
        </div>
      </div>
    </EmbedFrame>
  );
}
